<template>
  <div class="delivery-targets">
    <div class="box-title">
      <h4>
        <i class="fal fa-tachometer-alt"></i>
        {{ $t('deliverySpeed.settings_title') }}
      </h4>
    </div>

    <p class="setting-hint">{{ $t('deliverySpeed.settings_hint') }}</p>

    <div v-if="loadError" class="notification is-warning setting-alert">
      <i class="fal fa-exclamation-triangle"></i>
      {{ $t('deliverySpeed.settings_load_error') }}
      <vs-button transparent class="setting-alert-action" @click="fetchData()">{{ $t('deliverySpeed.retry') }}</vs-button>
    </div>
    <div v-else-if="loaded && !isReadable" class="notification is-warning setting-alert">
      <i class="fal fa-exclamation-triangle"></i>
      {{ $t('deliverySpeed.settings_unreadable') }}
    </div>
    <div v-else-if="loaded && !isPersisted" class="notification is-warning setting-alert">
      <i class="fal fa-database"></i>
      {{ $t('deliverySpeed.settings_not_persisted') }}
    </div>
    <div v-if="cacheStale" class="notification is-warning setting-alert">
      <i class="fal fa-clock"></i>
      {{ $t('deliverySpeed.settings_cache_stale') }}
    </div>

    <template v-if="loaded">
      <h5 class="setting-section">{{ $t('deliverySpeed.settings_lanes_title') }}</h5>
      <table class="table is-narrow lanes-table">
        <tbody>
          <tr v-for="lane in laneKeys" :key="lane">
            <td>{{ $t('deliverySpeed.lane_' + lane) }}</td>
            <td class="target-cell">
              <input
                  v-model="draft[cellKey(lane, 0)]"
                  type="text"
                  inputmode="numeric"
                  class="input is-small target-input"
                  :class="cellClass(cellKey(lane, 0))"
                  :title="serverErrors[cellKey(lane, 0)]"
                  :disabled="!canEdit || saving"
                  :aria-label="$t('deliverySpeed.lane_' + lane)"
              >
            </td>
          </tr>
        </tbody>
      </table>

      <h5 class="setting-section">{{ $t('deliverySpeed.settings_provinces_title') }}</h5>
      <p class="setting-hint">{{ $t('deliverySpeed.settings_provinces_hint') }}</p>
      <div class="table-container">
        <table class="table is-narrow is-fullwidth provinces-table">
          <thead>
            <tr>
              <th>{{ $t('deliverySpeed.col_province') }}</th>
              <th v-for="column in columns" :key="column.lane" class="has-text-centered">{{ $t('deliverySpeed.' + column.label) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="province in provinces" :key="province.province_id">
              <td>{{ province.name }}</td>
              <td v-for="column in columns" :key="column.lane" class="target-cell">
                <input
                    v-model="draft[cellKey(column.lane, province.province_id)]"
                    type="text"
                    inputmode="numeric"
                    class="input is-small target-input"
                    :class="cellClass(cellKey(column.lane, province.province_id))"
                    :title="serverErrors[cellKey(column.lane, province.province_id)]"
                    :placeholder="draft[cellKey(column.lane, 0)]"
                    :disabled="!canEdit || saving"
                    :aria-label="province.name + ': ' + $t('deliverySpeed.' + column.label)"
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-if="invalidKeys.length || rejected" class="setting-error">{{ $t('deliverySpeed.settings_invalid', {min: minDays, max: maxDays}) }}</p>

      <div class="setting-actions">
        <vs-button :loading="saving" :disabled="!canSave" @click="save()">
          <i class="fal fa-check-circle"></i>
          {{ $t('deliverySpeed.settings_save') }}
        </vs-button>
        <vs-button dark transparent :disabled="saving || !changedKeys.length" @click="reset()">
          {{ $t('deliverySpeed.settings_cancel') }}
        </vs-button>
      </div>
    </template>

    <Loading v-if="isLoading"/>
  </div>
</template>

<script>
import Loading from '@/views/Loading';

const LANES = ['vte_internal', 'province_internal', 'vte_to_province', 'province_to_vte', 'province_to_province'];
const COLUMNS = [
  {lane: 'province_internal', label: 'settings_col_within'},
  {lane: 'vte_to_province', label: 'settings_col_from_capital'},
  {lane: 'province_to_vte', label: 'settings_col_to_capital'},
  {lane: 'province_to_province', label: 'settings_col_from_provinces'},
];

/**
 * GET/PUT api/admin/delivery-speed-targets. A blank province cell follows its lane. Only changed
 * cells are sent, so a tab left open cannot revert what another admin changed elsewhere.
 */
export default {
  name: 'DeliverySpeedTargets',

  components: {Loading},

  data: () => ({
    isLoading: false,
    saving: false,
    loaded: false,
    loadError: false,
    isPersisted: false,
    isReadable: true,
    cacheStale: false,
    minDays: 1,
    maxDays: 30,
    laneKeys: LANES,
    columns: COLUMNS,
    provinces: [],
    baseline: {},
    draft: {},
    /// Laravel keys a 422 by position in what was sent, so the page keeps what it sent.
    lastSent: [],
    serverErrors: {},
    rejected: false,
  }),

  created() {
    this.fetchData();
  },

  computed: {
    canEdit() {
      return this.isPersisted && this.isReadable;
    },
    changedKeys() {
      return Object.keys(this.baseline).filter((key) => this.normal(this.draft[key]) !== this.baseline[key]);
    },
    invalidKeys() {
      return this.changedKeys.filter((key) => !this.isValid(key));
    },
    canSave() {
      return this.canEdit && !this.saving && this.changedKeys.length > 0 && this.invalidKeys.length === 0;
    },
  },

  watch: {
    /// A rejection describes what was sent. Once the admin edits, it no longer applies.
    draft: {
      deep: true,
      handler() {
        this.serverErrors = {};
        this.rejected = false;
      },
    },
  },

  methods: {
    cellKey(lane, provinceId) {
      return `${lane}|${provinceId}`;
    },
    /// Plain text, not a number input: a number input reports a half-typed "1e" as empty, which
    /// here would mean "follows the lane" and clear the province's target. "03" is 3, unchanged.
    normal(value) {
      const text = value === null || value === undefined ? '' : String(value).trim();
      return /^\d+$/.test(text) ? String(Number(text)) : text;
    },
    isValid(key) {
      const value = this.normal(this.draft[key]);
      if (value === '') return !key.endsWith('|0'); // a lane must keep a target
      const days = Number(value);
      return /^\d+$/.test(value) && days >= this.minDays && days <= this.maxDays;
    },
    cellClass(key) {
      return {
        'is-danger': !!this.serverErrors[key] || (this.normal(this.draft[key]) !== this.baseline[key] && !this.isValid(key)),
        'is-changed': this.normal(this.draft[key]) !== this.baseline[key],
      };
    },
    apply(data) {
      this.isPersisted = !!data.is_persisted;
      this.isReadable = data.is_readable !== false;
      this.minDays = data.min_days;
      this.maxDays = data.max_days;
      this.provinces = data.provinces || [];
      const cells = {};
      LANES.forEach((lane) => {
        cells[this.cellKey(lane, 0)] = this.normal((data.lanes || {})[lane]);
      });
      this.provinces.forEach((province) => {
        COLUMNS.forEach(({lane}) => {
          cells[this.cellKey(lane, province.province_id)] = this.normal(province.targets[lane]);
        });
      });
      this.baseline = cells;
      this.draft = {...cells};
      this.serverErrors = {};
      this.rejected = false;
      this.loaded = true;
    },
    reset() {
      this.draft = {...this.baseline};
      this.serverErrors = {};
      this.rejected = false;
    },
    fetchData() {
      this.isLoading = true;
      this.cacheStale = false;
      this.$axios.get('delivery-speed-targets').then((res) => {
        this.apply((res.data && res.data.data) || {});
        this.loadError = false;
      }).catch(() => {
        // say so rather than rendering empty inputs that look like real targets
        this.loaded = false;
        this.loadError = true;
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    save() {
      this.saving = true;
      this.serverErrors = {};
      this.rejected = false;
      this.lastSent = [...this.changedKeys];
      const targets = this.lastSent.map((key) => {
        const [lane, provinceId] = key.split('|');
        const value = this.normal(this.draft[key]);
        return {lane, province_id: Number(provinceId), days: value === '' ? null : Number(value)};
      });
      this.$axios.put('delivery-speed-targets', {targets}).then((res) => {
        this.apply((res.data && res.data.data) || {});
        this.cacheStale = !!(res.data && res.data.cache_stale);
        if (!this.cacheStale) {
          this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
        }
      }).catch((error) => {
        const status = error.response && error.response.status;
        if (status === 422) {
          this.showValidationErrors(error.response.data);
        } else if (status === 409) {
          this.isPersisted = false;
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      }).finally(() => {
        this.saving = false;
      });
    },
    showValidationErrors(body) {
      const errors = {};
      const fields = (body && body.errors) || {};
      Object.keys(fields).forEach((field) => {
        const match = field.match(/^targets\.(\d+)\./);
        const key = match && this.lastSent[Number(match[1])];
        if (key) errors[key] = [].concat(fields[field]).join(' ');
      });
      this.serverErrors = errors;
      this.rejected = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.delivery-targets {
  padding: 4px 2px 24px;
  text-align: left;
}

.setting-hint {
  color: #6b7280;
  line-height: 1.7;
  margin: 8px 0 4px;
}

.setting-alert {
  margin-top: 12px;

  i {
    margin-right: 6px;
  }
}

.setting-alert-action {
  display: inline-flex;
}

.setting-section {
  font-weight: 600;
  margin: 20px 0 8px;
}

.setting-error {
  color: #d03b3b;
  margin-top: 12px;
}

.setting-actions {
  display: flex;
  align-items: center;
  margin-top: 20px;

  i {
    margin-right: 6px;
  }
}

.lanes-table {
  max-width: 460px;

  td {
    vertical-align: middle;
  }
}

.provinces-table {
  th {
    white-space: nowrap;
    font-size: 13px;
  }

  td {
    vertical-align: middle;
  }
}

.target-cell {
  text-align: center;
  width: 110px;
}

.target-input {
  width: 72px;
  text-align: center;

  &.is-changed:not(.is-danger) {
    border-color: #2a78d6;
    font-weight: 600;
  }
}
</style>
